import React, { useState, useEffect } from 'react';

// Like Button Component cu animație
const LikeButton = ({ postId, likes = 0, isLiked = false, onLike }) => {
  const [liked, setLiked] = useState(isLiked);
  const [likeCount, setLikeCount] = useState(likes);
  const [isAnimating, setIsAnimating] = useState(false);

  // Sync with props
  useEffect(() => {
    setLiked(isLiked);
    setLikeCount(likes);
  }, [isLiked, likes]);

  const handleLike = async () => {
    setIsAnimating(true);
    const newLiked = !liked;
    const newCount = newLiked ? likeCount + 1 : Math.max(likeCount - 1, 0);
    
    setLiked(newLiked);
    setLikeCount(newCount);
    
    setTimeout(() => setIsAnimating(false), 300);
    
    if (onLike) {
      await onLike(postId, newLiked);
    }
  };

  return (
    <button
      onClick={handleLike}
      data-testid={`like-button-${postId}`}
      className={`group flex items-center space-x-2 px-3 py-2 rounded-full transition-all duration-200 hover:bg-pink-500/10 ${
        liked ? 'text-pink-500' : 'text-gray-500 hover:text-pink-500'
      }`}
      aria-label={liked ? 'Unlike' : 'Like'}
    >
      <div className={`relative ${isAnimating ? 'animate-pulse scale-110' : ''}`}>
        <svg className="w-5 h-5" fill={liked ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
        {isAnimating && (
          <div className="absolute inset-0 text-pink-500 animate-ping">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </div>
        )}
      </div>
      {likeCount > 0 && (
        <span className="text-sm font-medium">
          {likeCount.toLocaleString()}
        </span>
      )}
    </button>
  );
};

// Repost Button Component - Direct Quote action (X-style)
const RepostButton = ({ postId, reposts = 0, isReposted = false, onRepost, onQuote }) => {
  const [repostCount, setRepostCount] = useState(reposts);

  // Sync with props
  useEffect(() => {
    setRepostCount(reposts);
  }, [reposts]);

  const handleClick = (e) => {
    e.stopPropagation();
    // Direct Quote action
    if (onQuote) {
      onQuote(postId);
    }
  };

  return (
    <button
      onClick={handleClick}
      data-testid={`repost-button-${postId}`}
      className={`group flex items-center space-x-2 px-3 py-2 rounded-full transition-all duration-200 hover:bg-green-500/10 ${
        isReposted ? 'text-green-500' : 'text-gray-500 hover:text-green-500'
      }`}
      aria-label="Quote"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
      {repostCount > 0 && (
        <span className="text-sm font-medium">
          {repostCount.toLocaleString()}
        </span>
      )}
    </button>
  );
};

// Comment/Reply Button Component
const CommentButton = ({ postId, comments = 0, onComment }) => {
  const [commentCount, setCommentCount] = useState(comments);

  // Sync with props
  useEffect(() => {
    setCommentCount(comments);
  }, [comments]);

  const handleComment = () => {
    if (onComment) {
      onComment(postId);
    }
  };

  return (
    <button
      onClick={handleComment}
      data-testid={`comment-button-${postId}`}
      className="group flex items-center space-x-2 px-3 py-2 rounded-full transition-all duration-200 hover:bg-blue-500/10 text-gray-500 hover:text-blue-500"
      aria-label="Comment"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
      {commentCount > 0 && (
        <span className="text-sm font-medium">
          {commentCount.toLocaleString()}
        </span>
      )}
    </button>
  );
};

// Share Button Component
const ShareButton = ({ postId, onShare }) => {
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    // Copy link to clipboard
    const postUrl = `${window.location.origin}/post/${postId}`;
    
    try {
      await navigator.clipboard.writeText(postUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      
      if (onShare) {
        onShare(postId, postUrl);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
      if (onShare) {
        onShare(postId, postUrl);
      }
    }
  };

  return (
    <button
      onClick={handleShare}
      data-testid={`share-button-${postId}`}
      className={`group flex items-center space-x-2 px-3 py-2 rounded-full transition-all duration-200 hover:bg-cyan-500/10 ${
        copied ? 'text-cyan-500' : 'text-gray-500 hover:text-cyan-500'
      }`}
      aria-label="Share"
    >
      {copied ? (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
        </svg>
      )}
      {copied && <span className="text-xs">Copiat!</span>}
    </button>
  );
};

// Post Interactions Bar - receives interactions object
export const PostInteractions = ({ 
  postId, 
  interactions = {},
  onLike, 
  onRepost,
  onQuote,
  onComment, 
  onShare 
}) => {
  const {
    likes = 0,
    like_count = 0,
    reposts = 0,
    repost_count = 0,
    comments = 0,
    comments_count = 0,
    isLiked = false,
    liked = false,
    isReposted = false,
    reposted = false
  } = interactions;

  // Use either format (likes/like_count, isLiked/liked)
  const actualLikes = likes || like_count;
  const actualReposts = reposts || repost_count;
  const actualComments = comments || comments_count;
  const actualIsLiked = isLiked || liked;
  const actualIsReposted = isReposted || reposted;

  return (
    <div className="flex items-center justify-between max-w-md px-2" data-testid={`post-interactions-${postId}`}>
      <CommentButton 
        postId={postId} 
        comments={actualComments} 
        onComment={onComment} 
      />
      <RepostButton 
        postId={postId} 
        reposts={actualReposts} 
        isReposted={actualIsReposted}
        onRepost={onRepost}
        onQuote={onQuote}
      />
      <LikeButton 
        postId={postId} 
        likes={actualLikes} 
        isLiked={actualIsLiked}
        onLike={onLike} 
      />
      <ShareButton 
        postId={postId} 
        onShare={onShare} 
      />
    </div>
  );
};

// Follow Button Component
export const FollowButton = ({ userId, isFollowing = false, onFollow, size = 'normal' }) => {
  const [following, setFollowing] = useState(isFollowing);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setFollowing(isFollowing);
  }, [isFollowing]);

  const handleClick = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    const newFollowing = !following;
    setFollowing(newFollowing);
    
    try {
      if (onFollow) {
        await onFollow(userId, newFollowing);
      }
    } catch (error) {
      setFollowing(!newFollowing); // Revert on error
    } finally {
      setIsLoading(false);
    }
  };

  const sizeClasses = size === 'small' 
    ? 'px-3 py-1 text-xs'
    : 'px-4 py-2 text-sm';

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      data-testid={`follow-button-${userId}`}
      className={`${sizeClasses} font-semibold rounded-full transition-all duration-200 ${
        following
          ? 'bg-transparent border border-gray-500 text-gray-300 hover:border-red-500 hover:text-red-500 hover:bg-red-500/10'
          : 'bg-white text-black hover:bg-gray-200'
      } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {isLoading ? '...' : following ? 'Following' : 'Follow'}
    </button>
  );
};

// Trending Hashtags Component
export const TrendingHashtags = ({ hashtags = [], onHashtagClick }) => {
  if (!hashtags || hashtags.length === 0) {
    return null;
  }

  return (
    <div className="bg-gray-800/50 rounded-2xl p-4" data-testid="trending-hashtags">
      <h3 className="text-xl font-bold mb-4 text-white">Trending in Romania</h3>
      <div className="space-y-3">
        {hashtags.map((item, index) => (
          <div 
            key={item.tag || item.hashtag || index} 
            className="hover:bg-gray-700/50 rounded-lg p-2 -mx-2 cursor-pointer transition-colors"
            onClick={() => onHashtagClick && onHashtagClick(item.tag || item.hashtag)}
          >
            <div className="text-xs text-gray-500">#{index + 1} Trending</div>
            <div className="font-bold text-white">#{item.tag || item.hashtag?.replace('#', '')}</div>
            <div className="text-xs text-gray-500">{item.posts || item.post_count || 0} posts</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// User Suggestions Component
export const UserSuggestions = ({ users = [], onUserClick, onFollow }) => {
  if (!users || users.length === 0) {
    return null;
  }

  return (
    <div className="bg-gray-800/50 rounded-2xl p-4" data-testid="user-suggestions">
      <h3 className="text-xl font-bold mb-4 text-white">Who to follow</h3>
      <div className="space-y-4">
        {users.map((user) => (
          <div key={user.id} className="flex items-center justify-between">
            <div 
              className="flex items-center space-x-3 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => onUserClick && onUserClick(user.id)}
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white font-bold">
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt={user.name || user.nickname} className="w-full h-full rounded-full object-cover" />
                ) : (
                  (user.name?.[0] || user.nickname?.[0] || 'U').toUpperCase()
                )}
              </div>
              <div>
                <div className="font-semibold text-white text-sm">{user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown'}</div>
                <div className="text-gray-500 text-xs">@{user.nickname || 'user'}</div>
              </div>
            </div>
            <FollowButton
              userId={user.id}
              isFollowing={user.isFollowing || user.is_following}
              onFollow={onFollow}
              size="small"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// Comments Modal Component
export const CommentsModal = ({ 
  isOpen, 
  onClose, 
  postId, 
  postContent,
  comments = [],
  onAddComment,
  onDeleteComment,
  currentUserId,
  isLoading = false
}) => {
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onAddComment(postId, newComment.trim());
      setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'acum';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    return `${days}d`;
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-gray-900 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <h2 className="text-xl font-bold text-white">Comentarii</h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded-full transition-colors"
          >
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Original Post Preview */}
        {postContent && (
          <div className="p-4 border-b border-gray-800 bg-gray-800/30">
            <p className="text-gray-300 text-sm line-clamp-2">{postContent}</p>
          </div>
        )}

        {/* Comments List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>Niciun comentariu inca.</p>
              <p className="text-sm">Fii primul care comenteaza!</p>
            </div>
          ) : (
            comments.map((comment) => (
              <div key={comment.id} className="flex space-x-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  {comment.user_avatar ? (
                    <img src={comment.user_avatar} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    (comment.user_name?.[0] || 'U').toUpperCase()
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-white text-sm">{comment.user_name}</span>
                    <span className="text-gray-500 text-xs">@{comment.user_nickname}</span>
                    <span className="text-gray-600 text-xs">{formatDate(comment.created_at)}</span>
                  </div>
                  <p className="text-gray-200 text-sm mt-1">{comment.content}</p>
                  {comment.user_id === currentUserId && (
                    <button
                      onClick={() => onDeleteComment && onDeleteComment(postId, comment.id)}
                      className="text-xs text-red-400 hover:text-red-300 mt-1"
                    >
                      Sterge
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Add Comment Form */}
        <form onSubmit={handleSubmit} className="p-4 border-t border-gray-800">
          <div className="flex space-x-3">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Scrie un comentariu..."
              className="flex-1 bg-gray-800 border border-gray-700 rounded-full px-4 py-2 text-white text-sm focus:outline-none focus:border-cyan-500 transition-colors"
              maxLength={500}
            />
            <button
              type="submit"
              disabled={!newComment.trim() || isSubmitting}
              className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-full font-semibold text-sm transition-colors"
            >
              {isSubmitting ? '...' : 'Posteaza'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Quote Modal Component - for creating quote tweets
export const QuoteModal = ({ 
  isOpen, 
  onClose, 
  originalPost,
  onSubmitQuote,
  isSubmitting = false
}) => {
  const [quoteText, setQuoteText] = useState('');

  if (!isOpen || !originalPost) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    try {
      await onSubmitQuote(originalPost.id, quoteText.trim());
      setQuoteText('');
      onClose();
    } catch (error) {
      console.error('Error submitting quote:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'acum';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    return `${days}d`;
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-gray-900 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <h2 className="text-xl font-bold text-white">Quote</h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded-full transition-colors"
          >
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Quote Form */}
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
          {/* Text Input Area */}
          <div className="p-4 flex-1">
            <textarea
              value={quoteText}
              onChange={(e) => setQuoteText(e.target.value)}
              placeholder="Adaugă comentariul tău..."
              className="w-full h-24 bg-transparent text-white text-lg resize-none focus:outline-none placeholder-gray-500"
              maxLength={280}
              autoFocus
            />
            
            {/* Character count */}
            <div className="text-right text-sm text-gray-500">
              {quoteText.length}/280
            </div>
          </div>

          {/* Quoted Post Preview (Embedded) */}
          <div className="mx-4 mb-4">
            <QuotedPostEmbed post={originalPost} />
          </div>

          {/* Submit Button */}
          <div className="p-4 border-t border-gray-800">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-4 py-3 bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-full font-semibold transition-colors"
            >
              {isSubmitting ? 'Se postează...' : 'Quote'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Quoted Post Embed Component - shows the original post inside a quote
export const QuotedPostEmbed = ({ post, onClick }) => {
  if (!post) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'acum';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    return `${days}d`;
  };

  return (
    <div 
      className="border border-gray-700 rounded-xl p-3 hover:bg-gray-800/50 transition-colors cursor-pointer"
      onClick={onClick}
      data-testid={`quoted-post-${post.id}`}
    >
      {/* Quoted Post Header */}
      <div className="flex items-center space-x-2 mb-2">
        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
          {post.user_avatar ? (
            <img src={post.user_avatar} alt="" className="w-full h-full rounded-full object-cover" />
          ) : (
            (post.user_name?.[0] || post.user_nickname?.[0] || 'U').toUpperCase()
          )}
        </div>
        <span className="font-semibold text-white text-sm">{post.user_name}</span>
        <span className="text-gray-500 text-sm">@{post.user_nickname}</span>
        <span className="text-gray-600 text-sm">· {formatDate(post.created_at)}</span>
      </div>

      {/* Quoted Post Content */}
      <p className="text-gray-200 text-sm line-clamp-3">{post.content}</p>

      {/* Quoted Post Image (if any) */}
      {post.images && post.images.length > 0 && (
        <div className="mt-2 rounded-lg overflow-hidden max-h-32">
          <img 
            src={`${process.env.REACT_APP_BACKEND_URL}${post.images[0].thumbnail_url}`}
            alt="Post image"
            className="w-full h-full object-cover"
          />
        </div>
      )}
    </div>
  );
};

// Additional exports for internal components
export {
  LikeButton,
  RepostButton,
  CommentButton,
  ShareButton
};
